import { supabase } from '../../core/config/supabase';
import { FriendshipEntity, FriendshipWithUserEntity } from '../entities';
import { logger } from '@/src/core/utils';

export class FriendshipDataSource {
  async create(requesterId: string, addresseeId: string): Promise<FriendshipEntity> {
    const { data, error } = await supabase
      .from('friendships')
      .insert({ requester_id: requesterId, addressee_id: addresseeId, status: 'pending' })
      .select()
      .single();

    if (error) throw new Error(error.message);
    if (!data) throw new Error('No data returned from insert');

    return data as FriendshipEntity;
  }

  async update(friendshipId: string, status: 'accepted' | 'declined'): Promise<FriendshipEntity> {
    const { data, error } = await supabase
      .from('friendships')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', friendshipId)
      .select()
      .single();

    if (error) throw new Error(error.message);
    if (!data) throw new Error('No data returned from update');

    return data as FriendshipEntity;
  }

  async delete(friendshipId: string): Promise<void> {
    const { error } = await supabase.from('friendships').delete().eq('id', friendshipId);

    if (error) throw new Error(error.message);
  }

  async getFriends(
    userId: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<FriendshipWithUserEntity[]> {
    logger.debug('FriendshipDataSource', 'Calling RPC', { functionName: 'get_friends' });

    const { data, error } = await supabase.rpc('get_friends', {
      target_user_id: userId,
      limit_count: limit,
      offset_count: offset,
    });

    if (error) {
      logger.error('FriendshipDataSource', 'getFriends failed', { error });
      throw new Error(error.message);
    }

    logger.debug('FriendshipDataSource', 'Mapped items', { count: data?.length ?? 0 });

    return (data as FriendshipWithUserEntity[]) || [];
  }

  async getPendingRequests(
    userId: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<FriendshipWithUserEntity[]> {
    logger.debug('FriendshipDataSource', 'Calling RPC', { functionName: 'get_pending_friend_requests' });

    const { data, error } = await supabase.rpc('get_pending_friend_requests', {
      target_user_id: userId,
      limit_count: limit,
      offset_count: offset,
    });

    if (error) {
      logger.error('FriendshipDataSource', 'getPendingRequests failed', { error });
      throw new Error(error.message);
    }

    return (data as FriendshipWithUserEntity[]) || [];
  }

  async areFriends(userId1: string, userId2: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('friendships')
      .select('id')
      .or(
        `and(requester_id.eq.${userId1},addressee_id.eq.${userId2}),and(requester_id.eq.${userId2},addressee_id.eq.${userId1})`
      )
      .eq('status', 'accepted')
      .maybeSingle();

    if (error) throw new Error(error.message);

    return data !== null;
  }

  async getPendingRequestBetween(
    userId1: string,
    userId2: string
  ): Promise<FriendshipEntity | null> {
    const { data, error } = await supabase
      .from('friendships')
      .select('*')
      .or(
        `and(requester_id.eq.${userId1},addressee_id.eq.${userId2}),and(requester_id.eq.${userId2},addressee_id.eq.${userId1})`
      )
      .eq('status', 'pending')
      .maybeSingle();

    if (error) throw new Error(error.message);

    return data as FriendshipEntity | null;
  }
}
